/**
 * Event module attachment delivery helpers.
 * Staff users open attachments in a new tab; external company portal users download.
 */

export function coerceToBlob(data, mimeType = 'application/octet-stream') {
  if (data instanceof Blob) {
    if (mimeType && (!data.type || data.type === 'application/octet-stream')) {
      return new Blob([data], { type: mimeType });
    }
    return data;
  }
  if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
    return new Blob([data], { type: mimeType });
  }
  return new Blob([data], { type: mimeType });
}

export async function readBlobJsonMessage(blob, fallback = 'Request failed') {
  if (!(blob instanceof Blob)) return fallback;
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text);
    return String(parsed?.message || parsed?.error || fallback);
  } catch {
    return fallback;
  }
}

/** Reject JSON/HTML error payloads and non-PDF bodies returned with responseType blob. */
export async function assertRenderablePdfBlob(blob, fallback = 'Could not open PDF') {
  if (!(blob instanceof Blob) || blob.size < 5) {
    throw new Error(fallback);
  }
  if (blob.type?.includes('json')) {
    throw new Error(await readBlobJsonMessage(blob, fallback));
  }

  const header = await blob.slice(0, 5).text();
  if (header.startsWith('%PDF-')) return blob;

  const trimmed = header.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('<')) {
    throw new Error(await readBlobJsonMessage(blob, fallback));
  }

  throw new Error('Downloaded file is not a valid PDF');
}

export function openBlobInNewTab(data, mimeType = 'application/octet-stream') {
  const blob = coerceToBlob(data, mimeType);
  const url = URL.createObjectURL(blob);
  // Do not pass `noopener` in windowFeatures — Chromium then returns null even when the
  // tab opened, which incorrectly triggers a fallback and opens a second tab.
  const opened = window.open(url, '_blank');
  if (opened) {
    try {
      opened.opener = null;
    } catch {
      /* ignore */
    }
  } else {
    // Popup blocked — download instead of <a target="_blank">, which in some embedded
    // browsers navigates to a Google search for the blob: URL.
    downloadBlob(blob, 'document', mimeType);
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function openPdfBlobInNewTab(data, { fallbackMessage = 'Could not open PDF' } = {}) {
  const blob = await assertRenderablePdfBlob(coerceToBlob(data, 'application/pdf'), fallbackMessage);
  openBlobInNewTab(blob, 'application/pdf');
}

/** File name for a saved invoice PDF: the invoice number with unsafe characters replaced, plus .pdf */
export function invoicePdfFileName(invoiceNumber, fallback = 'invoice') {
  const base = String(invoiceNumber || fallback).trim().replace(/[\/?%*:|"<>\s]+/g, '-');
  return `${base || fallback}.pdf`;
}

const MIME_EXTENSIONS = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/heic': 'heic',
};

/**
 * File name for a saved payment attachment (bank slip etc.): "<payment ref>-slip.<ext>",
 * numbered when a payment has several attachments. Extension comes from the original file name or type.
 */
export function paymentAttachmentFileName({ reference = '', doc = {}, index = 0, total = 1 } = {}) {
  const original = String(doc?.originalName || doc?.fileName || '');
  const dot = original.lastIndexOf('.');
  const extFromName = dot > 0 && dot < original.length - 1 ? original.slice(dot + 1).toLowerCase() : '';
  const ext = extFromName || MIME_EXTENSIONS[String(doc?.fileType || doc?.mimeType || '').toLowerCase()] || 'pdf';
  const ref = String(reference || '').trim().replace(/[/\\?%*:|"<>\s]+/g, '-');
  const base = ref ? `${ref}-slip` : 'payment-slip';
  return `${base}${total > 1 ? `-${index + 1}` : ''}.${ext}`;
}

export function downloadBlob(data, filename, mimeType = 'application/octet-stream') {
  const blob = coerceToBlob(data, mimeType);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'document';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** @param {'open' | 'download'} deliveryMode */
export function deliverAttachmentBlob(data, { fileName, mimeType = 'application/octet-stream', deliveryMode = 'open' } = {}) {
  if (deliveryMode === 'download') {
    downloadBlob(data, fileName || 'document', mimeType);
  } else {
    openBlobInNewTab(data, mimeType);
  }
}

/** Resolve attachment delivery from portal context. */
export function resolveAttachmentDeliveryMode({ preferDownload = false } = {}) {
  return preferDownload ? 'download' : 'open';
}
